import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Shield, Users, Package, Tag, ShoppingCart, TrendingUp,
    Ban, Trash2, Coins, LogOut as LogOutIcon, Plus, Pencil,
    Loader2, ArrowLeft, RefreshCw, CheckCircle2, XCircle,
} from "lucide-react";
import Logo from "@/components/Logo";
import EditProductModal from "@/components/EditProductModal";

function Kpi({ icon: Icon, label, value, color = "#FF5722", testId }) {
    return (
        <div className="glass rounded-2xl p-4" data-testid={testId}>
            <div className="flex items-center justify-between">
                <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <p className="font-display font-black text-3xl mt-2">{value}</p>
            <p className="text-xs text-white/60 font-body mt-1">{label}</p>
        </div>
    );
}

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [orders, setOrders] = useState([]);
    const [txns, setTxns] = useState([]);
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [tab, setTab] = useState("dashboard");
    const [busy, setBusy] = useState(false);

    // Category dialog
    const [catDialog, setCatDialog] = useState(false);
    const [editingCat, setEditingCat] = useState(null);
    const [catForm, setCatForm] = useState({ label: "", slug: "", icon: "package", order: 100 });

    // Product edit
    const [editingProduct, setEditingProduct] = useState(null);

    // Credit dialog
    const [creditDialog, setCreditDialog] = useState(false);
    const [creditUser, setCreditUser] = useState(null);
    const [creditAmount, setCreditAmount] = useState(10);
    const [creditReason, setCreditReason] = useState("");

    useEffect(() => {
        if (!loading) {
            if (!user) navigate("/login");
        }
    }, [user, loading, navigate]);

    const loadAll = useCallback(async () => {
        setBusy(true);
        try {
            const [s, u, p, c, o, t] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/users", { params: { q, role: roleFilter || undefined } }),
                api.get("/admin/products", { params: { q } }),
                api.get("/categories"),
                api.get("/admin/orders"),
                api.get("/admin/transactions"),
            ]);
            setStats(s.data);
            setUsers(u.data);
            setProducts(p.data);
            setCategories(c.data);
            setOrders(o.data);
            setTxns(t.data);
        } catch (err) {
            console.error("Admin load error", err);
            toast.error("Erreur de chargement");
        } finally {
            setBusy(false);
        }
    }, [q, roleFilter]);

    useEffect(() => {
        if (user?.is_admin) loadAll();
    }, [user, loadAll]);

    useEffect(() => {
        if (!user?.is_admin) return;
        const t = setTimeout(() => {
            api.get("/admin/users", { params: { q, role: roleFilter || undefined } })
                .then((r) => setUsers(r.data))
                .catch((err) => console.debug("users fetch failed", err?.response?.status));
            api.get("/admin/products", { params: { q } })
                .then((r) => setProducts(r.data))
                .catch((err) => console.debug("products fetch failed", err?.response?.status));
        }, 300);
        return () => clearTimeout(t);
    }, [q, roleFilter, user]);

    const blockUser = async (uid, currentBlocked) => {
        try {
            const url = currentBlocked ? `/admin/users/${uid}/unblock` : `/admin/users/${uid}/block`;
            await api.post(url);
            toast.success(currentBlocked ? "Débloqué" : "Bloqué");
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const forceLogout = async (uid) => {
        try {
            await api.post(`/admin/users/${uid}/force-logout`);
            toast.success("Sessions invalidées");
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const deleteUser = async (uid) => {
        if (!window.confirm("Supprimer définitivement cet utilisateur et ses données ?")) return;
        try {
            await api.delete(`/admin/users/${uid}`);
            toast.success("Utilisateur supprimé");
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const toggleRole = async (uid, currentRole) => {
        const newRole = currentRole === "seller" ? "client" : "seller";
        try {
            await api.post(`/admin/users/${uid}/role`, { role: newRole });
            toast.success(`Rôle → ${newRole}`);
            loadAll();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const openCredit = (u) => {
        setCreditUser(u);
        setCreditAmount(10);
        setCreditReason("");
        setCreditDialog(true);
    };

    const submitCredit = async () => {
        try {
            await api.post(`/admin/users/${creditUser.user_id}/credits`, {
                amount: Number(creditAmount),
                reason: creditReason,
            });
            toast.success(`${creditAmount > 0 ? "+" : ""}${creditAmount} crédits`);
            setCreditDialog(false);
            loadAll();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Supprimer ce produit ?")) return;
        try {
            await api.delete(`/admin/products/${id}`);
            toast.success("Produit supprimé");
            loadAll();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const openCat = (c) => {
        if (c) {
            setEditingCat(c);
            setCatForm({ label: c.label, slug: c.slug, icon: c.icon, order: c.order || 100 });
        } else {
            setEditingCat(null);
            setCatForm({ label: "", slug: "", icon: "package", order: 100 });
        }
        setCatDialog(true);
    };

    const submitCat = async () => {
        try {
            if (editingCat) {
                await api.put(`/admin/categories/${editingCat.id}`, catForm);
                toast.success("Catégorie mise à jour");
            } else {
                await api.post("/admin/categories", catForm);
                toast.success("Catégorie créée");
            }
            setCatDialog(false);
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const deleteCat = async (id) => {
        if (!window.confirm("Supprimer cette catégorie ?")) return;
        try {
            await api.delete(`/admin/categories/${id}`);
            toast.success("Catégorie supprimée");
            loadAll();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    if (loading || !user) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-[#050505]">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" />
            </div>
        );
    }

    if (!user.is_admin) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-[#050505] text-white px-6">
                <div className="max-w-md text-center glass rounded-2xl p-6" data-testid="admin-denied">
                    <Shield className="w-10 h-10 text-[#FF5722] mx-auto mb-3" />
                    <h2 className="font-display font-black text-2xl mb-2">Accès refusé</h2>
                    <p className="text-white/70 font-body text-sm mb-4">
                        Vous êtes connecté en tant que <b>{user.email}</b>. Cet email n'est pas administrateur de VIDMAR.
                    </p>
                    <p className="text-white/50 font-body text-xs mb-4">
                        Si vous êtes l'administrateur, vérifiez que l'email <b>ADMIN_EMAILS</b> est bien configuré côté serveur, puis déconnectez-vous et reconnectez-vous.
                    </p>
                    <button
                        onClick={() => navigate("/feed")}
                        data-testid="admin-denied-back"
                        className="orange-btn h-11 px-6 rounded-full font-body font-bold"
                    >
                        Retour au feed
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-20 grain-bg">
            <div className="max-w-6xl mx-auto px-4 pt-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate("/feed")} data-testid="admin-back" className="w-10 h-10 glass rounded-full flex items-center justify-center">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <Logo size={40} />
                        <div>
                            <h1 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                                Admin <Shield className="w-5 h-5 text-[#FF5722]" />
                            </h1>
                            <p className="text-xs text-white/50 font-body">Contrôle de la plateforme VIDMAR</p>
                        </div>
                    </div>
                    <button onClick={loadAll} disabled={busy} data-testid="admin-refresh" className="w-10 h-10 glass rounded-full flex items-center justify-center">
                        <RefreshCw className={`w-5 h-5 ${busy ? "animate-spin" : ""}`} />
                    </button>
                </div>

                <Tabs value={tab} onValueChange={setTab} className="w-full">
                    <TabsList className="bg-white/5 border border-white/10 rounded-full p-1 flex flex-wrap w-auto">
                        <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Tableau</TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Utilisateurs</TabsTrigger>
                        <TabsTrigger value="products" data-testid="tab-products" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Produits</TabsTrigger>
                        <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Catégories</TabsTrigger>
                        <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Commandes</TabsTrigger>
                        <TabsTrigger value="txns" data-testid="tab-txns" className="rounded-full font-body data-[state=active]:bg-[#FF5722] data-[state=active]:text-white">Paiements</TabsTrigger>
                    </TabsList>

                    {/* DASHBOARD */}
                    <TabsContent value="dashboard" className="mt-6">
                        {stats && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Kpi icon={Users} label="Utilisateurs" value={stats.users} testId="kpi-users" color="#FFFFFF" />
                                <Kpi icon={Users} label="Vendeurs" value={stats.sellers} testId="kpi-sellers" color="#FF5722" />
                                <Kpi icon={Users} label="Clients" value={stats.clients} testId="kpi-clients" color="#25D366" />
                                <Kpi icon={Ban} label="Bloqués" value={stats.blocked} testId="kpi-blocked" color="#EF4444" />
                                <Kpi icon={Package} label="Produits" value={stats.products} testId="kpi-products" color="#FFC107" />
                                <Kpi icon={ShoppingCart} label="Commandes WA" value={stats.orders} testId="kpi-orders" color="#25D366" />
                                <Kpi icon={Coins} label="Achats crédits" value={stats.transactions} testId="kpi-txns" color="#FFC107" />
                                <Kpi icon={TrendingUp} label="Revenu (FCFA)" value={Number(stats.revenue_fcfa).toLocaleString("fr-FR")} testId="kpi-revenue" color="#25D366" />
                            </div>
                        )}
                    </TabsContent>

                    {/* USERS */}
                    <TabsContent value="users" className="mt-6">
                        <div className="flex flex-col md:flex-row gap-3 mb-4">
                            <Input
                                data-testid="admin-users-search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Rechercher par nom, email, boutique..."
                                className="bg-white/5 border-white/10 h-11 text-white"
                            />
                            <div className="flex gap-2">
                                {["", "client", "seller"].map((r) => (
                                    <button
                                        key={r || "all"}
                                        data-testid={`admin-filter-${r || "all"}`}
                                        onClick={() => setRoleFilter(r)}
                                        className={`px-4 h-11 rounded-full font-body text-sm ${roleFilter === r ? "bg-[#FF5722] text-white" : "bg-white/5 text-white/70 border border-white/10"}`}
                                    >
                                        {r === "" ? "Tous" : r === "client" ? "Clients" : "Vendeurs"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            {users.map((u) => (
                                <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="glass rounded-xl p-3 flex items-center gap-3 flex-wrap">
                                    <div className="w-11 h-11 rounded-full bg-white/10 overflow-hidden flex items-center justify-center shrink-0">
                                        {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold">{u.name?.charAt(0)}</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-body font-semibold text-sm truncate flex items-center gap-2">
                                            {u.shop_name || u.name}
                                            {u.is_admin && <Badge className="bg-[#FF5722]/20 text-[#FF5722] border-[#FF5722]/30 text-[10px]">Admin</Badge>}
                                            {u.is_blocked && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">Bloqué</Badge>}
                                        </p>
                                        <p className="text-[11px] text-white/50 font-body truncate">{u.email}</p>
                                        <div className="flex gap-2 mt-1 text-[10px] text-white/60 font-body">
                                            <span>Role: <b className="text-white">{u.role}</b></span>
                                            {u.role === "seller" && <span>Crédits: <b className="text-[#FFC107]">{u.credits ?? 0}</b></span>}
                                            {u.city && <span>{u.city}</span>}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                        <button data-testid={`credits-${u.user_id}`} onClick={() => openCredit(u)} className="w-9 h-9 rounded-full bg-[#FFC107]/20 border border-[#FFC107]/30 text-[#FFC107] flex items-center justify-center hover:bg-[#FFC107]/30" title="Crédits">
                                            <Coins className="w-4 h-4" />
                                        </button>
                                        <button data-testid={`role-${u.user_id}`} onClick={() => toggleRole(u.user_id, u.role)} className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-[11px] font-body">
                                            ⇄ {u.role === "seller" ? "→client" : "→vendeur"}
                                        </button>
                                        <button data-testid={`logout-${u.user_id}`} onClick={() => forceLogout(u.user_id)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center" title="Force logout">
                                            <LogOutIcon className="w-4 h-4" />
                                        </button>
                                        <button data-testid={`block-${u.user_id}`} onClick={() => blockUser(u.user_id, u.is_blocked)} className={`w-9 h-9 rounded-full flex items-center justify-center ${u.is_blocked ? "bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366]" : "bg-red-500/20 border border-red-500/30 text-red-400"}`} title={u.is_blocked ? "Débloquer" : "Bloquer"}>
                                            {u.is_blocked ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                        </button>
                                        <button data-testid={`delete-user-${u.user_id}`} onClick={() => deleteUser(u.user_id)} className="w-9 h-9 rounded-full bg-red-600/20 border border-red-600/40 text-red-400 flex items-center justify-center" title="Supprimer">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {users.length === 0 && <p className="text-center text-white/50 font-body py-8">Aucun utilisateur</p>}
                        </div>
                    </TabsContent>

                    {/* PRODUCTS */}
                    <TabsContent value="products" className="mt-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {products.map((p) => (
                                <div key={p.id} data-testid={`admin-product-${p.id}`} className="relative aspect-[3/4] rounded-xl overflow-hidden bg-white/5 group">
                                    <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent p-2 flex flex-col justify-end">
                                        <p className="font-body font-bold text-xs text-white truncate">{p.title}</p>
                                        <p className="text-[10px] text-white/70 truncate">{p.seller_name}</p>
                                        <p className="text-[10px] text-[#FFC107] font-bold">{Number(p.price).toLocaleString("fr-FR")} F</p>
                                        {p.is_deleted && <Badge className="bg-red-500/30 text-red-300 text-[9px] mt-1 w-fit">Supprimé</Badge>}
                                    </div>
                                    {!p.is_deleted && (
                                        <>
                                            <button onClick={() => setEditingProduct(p)} data-testid={`admin-edit-product-${p.id}`} className="absolute top-2 left-2 w-8 h-8 rounded-full bg-[#FF5722] flex items-center justify-center">
                                                <Pencil className="w-4 h-4 text-white" />
                                            </button>
                                            <button onClick={() => deleteProduct(p.id)} data-testid={`admin-delete-product-${p.id}`} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600/80 flex items-center justify-center">
                                                <Trash2 className="w-4 h-4 text-white" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                            {products.length === 0 && <p className="text-center text-white/50 font-body py-8 col-span-full">Aucun produit</p>}
                        </div>
                    </TabsContent>

                    {/* CATEGORIES */}
                    <TabsContent value="categories" className="mt-6">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-white/70 font-body text-sm">Gérer les catégories de produits</p>
                            <Button data-testid="add-cat-btn" onClick={() => openCat(null)} className="orange-btn rounded-full h-11">
                                <Plus className="w-4 h-4 mr-1" /> Nouvelle
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {categories.map((c) => (
                                <div key={c.id} data-testid={`cat-${c.id}`} className="glass rounded-xl p-3 flex items-center justify-between">
                                    <div>
                                        <p className="font-display font-bold">{c.label}</p>
                                        <p className="text-[11px] text-white/50 font-body">@{c.slug} · icône: {c.icon} · ordre: {c.order}</p>
                                    </div>
                                    <div className="flex gap-1">
                                        <button data-testid={`edit-cat-${c.id}`} onClick={() => openCat(c)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button data-testid={`delete-cat-${c.id}`} onClick={() => deleteCat(c.id)} className="w-9 h-9 rounded-full bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* ORDERS */}
                    <TabsContent value="orders" className="mt-6">
                        <div className="space-y-2">
                            {orders.map((o) => (
                                <div key={o.id} data-testid={`order-${o.id}`} className="glass rounded-xl p-3 flex items-center justify-between">
                                    <div>
                                        <p className="font-body font-semibold text-sm">Commande #{String(o.id || "").slice(-8)}</p>
                                        <p className="text-[11px] text-white/50 font-body">Client: {o.client_name || "Anonyme"} · Produit: {String(o.product_id || "").slice(-8)}</p>
                                        <p className="text-[11px] text-white/50 font-body">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
                                    </div>
                                    <Badge className="bg-[#25D366]/20 text-[#25D366] border-[#25D366]/30">-{o.credits_deducted} crédit</Badge>
                                </div>
                            ))}
                            {orders.length === 0 && <p className="text-center text-white/50 font-body py-8">Aucune commande</p>}
                        </div>
                    </TabsContent>

                    {/* TRANSACTIONS */}
                    <TabsContent value="txns" className="mt-6">
                        <div className="space-y-2">
                            {txns.map((t) => (
                                <div key={t.id} data-testid={`txn-${t.id}`} className="glass rounded-xl p-3 flex items-center justify-between">
                                    <div>
                                        <div className="font-body font-semibold text-sm flex items-center flex-wrap gap-1">
                                            <span>{t.credits_added > 0 ? "+" : ""}{t.credits_added} crédits</span>
                                            {t.is_mocked && <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-[9px]">MOCKED</Badge>}
                                            {t.provider === "admin" && <Badge className="ml-2 bg-[#FF5722]/20 text-[#FF5722] border-[#FF5722]/30 text-[9px]">ADMIN</Badge>}
                                        </div>
                                        <p className="text-[11px] text-white/50 font-body">{t.provider?.toUpperCase()} · {String(t.user_id || "").slice(-8)} · {new Date(t.created_at).toLocaleString("fr-FR")}</p>
                                        {t.reason && <p className="text-[10px] text-white/40 italic mt-1">« {t.reason} »</p>}
                                    </div>
                                    <p className="font-display font-bold text-[#FFC107]">{t.amount || 0} F</p>
                                </div>
                            ))}
                            {txns.length === 0 && <p className="text-center text-white/50 font-body py-8">Aucune transaction</p>}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Category Dialog */}
            <Dialog open={catDialog} onOpenChange={setCatDialog}>
                <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editingCat ? "Modifier catégorie" : "Nouvelle catégorie"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-white/80">Nom *</Label>
                            <Input data-testid="cat-label-input" value={catForm.label} onChange={(e) => setCatForm({ ...catForm, label: e.target.value })} className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                        </div>
                        <div>
                            <Label className="text-white/80">Slug (optionnel)</Label>
                            <Input data-testid="cat-slug-input" value={catForm.slug} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} placeholder="auto" className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-white/80">Icône</Label>
                                <Input value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                            </div>
                            <div>
                                <Label className="text-white/80">Ordre</Label>
                                <Input type="number" value={catForm.order} onChange={(e) => setCatForm({ ...catForm, order: e.target.value })} className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="cat-submit" onClick={submitCat} className="orange-btn rounded-full h-11 w-full">
                            {editingCat ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Credit Dialog */}
            <Dialog open={creditDialog} onOpenChange={setCreditDialog}>
                <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display">Ajuster les crédits</DialogTitle>
                    </DialogHeader>
                    {creditUser && (
                        <div className="space-y-3">
                            <p className="text-sm text-white/70 font-body">{creditUser.shop_name || creditUser.name} — solde actuel : <b className="text-[#FFC107]">{creditUser.credits ?? 0}</b></p>
                            <div>
                                <Label className="text-white/80">Montant (positif = ajouter, négatif = retirer)</Label>
                                <Input type="number" data-testid="credit-amount-input" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                            </div>
                            <div>
                                <Label className="text-white/80">Raison (optionnel)</Label>
                                <Input data-testid="credit-reason-input" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="Cadeau, correction, promotion..." className="mt-1 bg-white/5 border-white/10 h-11 text-white" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button data-testid="credit-submit" onClick={submitCredit} className="orange-btn rounded-full h-11 w-full">
                            Valider
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Product Edit Modal */}
            <EditProductModal
                product={editingProduct}
                open={!!editingProduct}
                onClose={() => setEditingProduct(null)}
                onUpdated={(updated) => {
                    setProducts((ps) => ps.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
                    setEditingProduct(null);
                }}
            />
        </div>
    );
}
